import React, { useState, useEffect, useMemo } from 'react';
import { getThingAlarmAnalytics } from '../../../../services';
import * as s from './styled-alarm-analytics';

// Analitica de alarmes da maquina no periodo: downtime total, MTBF, MTTR e
// Pareto das causas por TEMPO PARADO (nao so contagem). Vem do endpoint
// /thing/:id/alarm-analytics. Default 'week' (o backend recomenda day/week;
// month pode ser lento por causa da correlacao de causa por alarme).

const PERIODS = [
  { key: 'day', label: 'Dia' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
];

const fmtDuration = (sec) => {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  const s = Math.max(0, Math.floor(Number(sec)));
  if (s < 60) return `${s}s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    const rmin = min % 60;
    return rmin ? `${hr}h ${rmin}min` : `${hr}h`;
  }
  const day = Math.floor(hr / 24);
  const rhr = hr % 24;
  return rhr ? `${day}d ${rhr}h` : `${day}d`;
};

const prettyCause = (cause) => {
  if (!cause || cause === 'SEM_CAUSA_IDENTIFICADA') return 'Sem causa identificada';
  return cause;
};

const AlarmAnalytics = ({ thingId }) => {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (thingId == null) return undefined;
    let active = true;
    setLoading(true);
    getThingAlarmAnalytics(thingId, period)
      .then((res) => {
        if (!active) return;
        setData((res && res.data) || res || null);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setData(null);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [thingId, period]);

  const pareto = useMemo(() => {
    const list = (data && data.pareto) || [];
    const max = list.reduce(
      (m, p) => Math.max(m, Number(p.downtimeSeconds) || 0),
      0,
    );
    return { list: list.slice(0, 4), max };
  }, [data]);

  const hasAny =
    data &&
    ((data.failureCount || 0) > 0 || (data.totalDowntimeSeconds || 0) > 0);

  return (
    <s.Wrap>
      <s.Head>
        <s.HeadLabel>Analítica de alarmes</s.HeadLabel>
        <s.PeriodToggle>
          {PERIODS.map((p) => (
            <s.PeriodBtn
              key={p.key}
              type="button"
              $active={period === p.key}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </s.PeriodBtn>
          ))}
        </s.PeriodToggle>
      </s.Head>

      <s.Kpis>
        <s.Kpi>
          <s.KpiLabel>Tempo parado</s.KpiLabel>
          <s.KpiValue>
            {loading ? '—' : fmtDuration(data && data.totalDowntimeSeconds)}
          </s.KpiValue>
        </s.Kpi>
        <s.Kpi>
          <s.KpiLabel title="MTBF — tempo médio entre falhas">
            Tempo entre falhas
          </s.KpiLabel>
          <s.KpiValue title="Em média, a máquina opera este tempo entre uma falha e a próxima (MTBF)">
            {loading ? '—' : fmtDuration(data && data.mtbfSeconds)}
          </s.KpiValue>
        </s.Kpi>
        <s.Kpi>
          <s.KpiLabel title="MTTR — tempo médio de reparo">
            Tempo de reparo
          </s.KpiLabel>
          <s.KpiValue title="Em média, cada parada leva este tempo até voltar a operar (MTTR)">
            {loading ? '—' : fmtDuration(data && data.mttrSeconds)}
          </s.KpiValue>
        </s.Kpi>
        <s.Kpi>
          <s.KpiLabel>Falhas</s.KpiLabel>
          <s.KpiValue $alarm={(data && data.failureCount) > 0}>
            {loading ? '—' : (data && data.failureCount) || 0}
          </s.KpiValue>
        </s.Kpi>
      </s.Kpis>

      {!loading && hasAny && pareto.list.length > 0 && (
        <s.Pareto>
          <s.ParetoTitle>Causas por tempo parado</s.ParetoTitle>
          {pareto.list.map((p) => {
            const dt = Number(p.downtimeSeconds) || 0;
            const pct = pareto.max > 0 ? (dt / pareto.max) * 100 : 0;
            return (
              <s.ParetoRow key={p.cause}>
                <s.ParetoHead>
                  <s.ParetoCause title={prettyCause(p.cause)}>
                    {prettyCause(p.cause)}
                  </s.ParetoCause>
                  <s.ParetoMeta>
                    {fmtDuration(dt)} · {p.count}×
                  </s.ParetoMeta>
                </s.ParetoHead>
                <s.ParetoTrack>
                  <s.ParetoFill style={{ width: `${pct}%` }} />
                </s.ParetoTrack>
              </s.ParetoRow>
            );
          })}
        </s.Pareto>
      )}
      {!loading && !hasAny && (
        <s.Empty>Sem paradas registradas no período.</s.Empty>
      )}
    </s.Wrap>
  );
};

export default AlarmAnalytics;
